import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Video } from "lucide-react";
import { NAV_LINKS, TID, WHATSAPP_URL } from "@/constants/testIds";

const Logo = () => (
  <Link to="/" data-testid={TID.headerLogo} className="flex items-center gap-3 group" aria-label="Pali Hill City home">
    <img
      src="/pali-hills-logo.png"
      alt="Pali Hills — Your Peace of Land"
      width={132}
      height={42}
      className="h-10 md:h-11 w-auto select-none"
      draggable="false"
    />
    <span className="hidden sm:block leading-tight border-l border-forest/20 pl-3">
      <span className="eyebrow text-[10px] text-forest/70 block">Chapter III</span>
      <span className="text-[11px] text-forest/60">The Peace of Land</span>
    </span>
  </Link>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onEOI = pathname.startsWith("/eoi");
  if (onEOI) return null; // EOI landing page has no nav

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-md border-b"
      style={{
        background: "rgba(244,244,239,0.86)",
        borderColor: "rgba(11,74,61,0.12)",
      }}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-5 md:px-10 py-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-7 text-[13px] tracking-wide">
          {NAV_LINKS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `text-forest/80 hover:text-forest transition-colors ${
                  isActive ? "text-forest font-semibold" : ""
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="WhatsApp"
            className="text-forest/70 hover:text-forest w-9 h-9 grid place-items-center rounded-full border border-forest/20 hover:border-forest transition-colors"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
          </a>
          <Link
            to="/strategy-call"
            data-testid={TID.navStrategy}
            className="text-forest hover:text-teal text-[13px] hidden xl:inline"
          >
            Consultation
          </Link>
          <Link
            to="/event"
            data-testid={TID.navBookVisit}
            className="btn-primary text-[13px] py-2.5 px-4"
          >
            <Video size={14} /> Register for Launch
          </Link>
        </div>

        <button
          className="lg:hidden p-2 -mr-2 text-forest"
          onClick={() => setOpen((v) => !v)}
          data-testid={TID.mobileMenuBtn}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div
          data-testid={TID.mobileMenu}
          className="lg:hidden border-t px-5 py-6 space-y-4 bg-offwhite"
          style={{ borderColor: "rgba(11,74,61,0.12)" }}
        >
          {NAV_LINKS.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`m-${n.testid}`}
              className="block font-display text-2xl text-forest"
            >
              {n.label}
            </Link>
          ))}
          <Link
            to="/event"
            onClick={() => setOpen(false)}
            className="btn-primary w-full justify-center mt-4"
            data-testid={`m-${TID.navBookVisit}`}
          >
            <Video size={16} /> Register for Launch
          </Link>
          <Link
            to="/strategy-call"
            onClick={() => setOpen(false)}
            className="btn-outline-forest w-full justify-center mt-2"
            data-testid={`m-${TID.navStrategy}`}
          >
            Free Online Consultation
          </Link>
        </div>
      )}
    </header>
  );
}
